import { useMemo } from 'react';
import { IS_USE_PLEX_API, WIDGET_TYPES } from '../../constants';
import {
  CasinoTypeUnion,
  IGameWithPlaceholder,
  INormalizedGame
} from '../../types';
import { useCasinoGamesSectionPagination } from '../useCasinoGamesSectionPagination';
import { useGetGamesByFilter } from '../useGetGamesByFilter';
import { useGetIsCustomCategory } from '../useGetIsCustomCategory';
import {
  usePlexCategoryPagination,
  usePlexCustomCategoryPagination,
  usePlexProviderPagination
} from '../usePlexCasinoGamesSectionPagination';

interface FilteredMultiSectionGames {
  games: IGameWithPlaceholder[];
  sectionId: string;
  gamesIds: string[];
}

interface UseOldGamesFilterAndPaginationParams {
  category: string;
  provider: string;
  casinoType: CasinoTypeUnion;
  // make isPaginationEnabled catchAll optional until oldGamesFilterAndPagination gone
  isPaginationEnabled?: boolean;
  catchAll?: boolean;
}

type UseOldGamesFilterAndPagination = (
  params: UseOldGamesFilterAndPaginationParams
) => {
  games: INormalizedGame[];
  isFiltered: boolean;
  page?: number;
  filteredMultiSectionGames: FilteredMultiSectionGames[];
  handlePageChange?: (newPage: number) => void;
};

export const useOldGamesFilterAndPagination: UseOldGamesFilterAndPagination = ({
  category,
  provider,
  casinoType,
  isPaginationEnabled = false,
  catchAll
}) => {
  const { filteredGames, filteredMultiSectionGames, isFiltered } =
    useGetGamesByFilter({
      selectedCategory: category,
      selectedProvider: provider,
      casinoType,
      catchAll
    });
  const { page, handlePageChange } =
    useCasinoGamesSectionPagination(isPaginationEnabled);

  return {
    games: filteredGames,
    isFiltered,
    page,
    handlePageChange,
    filteredMultiSectionGames
  };
};

interface UsePlexGamesFilterAndPaginationParams {
  casinoType: CasinoTypeUnion;
  provider: string;
  category: string;
  // make pageSize until oldGamesFilterAndPagination gone
  pageSize?: number;
}

type UsePlexGamesFilterAndPagination = (
  params: UsePlexGamesFilterAndPaginationParams
) => {
  games: INormalizedGame[];
  isFiltered: boolean;
  page: number;
  filteredMultiSectionGames: FilteredMultiSectionGames[];
  handlePageChange: (newPage: number) => Promise<void>;
  isAllGamesLoaded: boolean;
  isLoading: boolean;
};

interface PaginationData {
  page: number;
  handlePageChange: (newPage: number) => Promise<void>;
  isAllGamesLoaded: boolean;
  games?: INormalizedGame[];
  isLoading: boolean;
  isInitialDataLoaded?: string | boolean;
  filteredMultiSectionGames?: FilteredMultiSectionGames[];
}

export const usePlexGamesFilterAndPagination: UsePlexGamesFilterAndPagination =
  ({ casinoType, provider, category, pageSize }) => {
    const { isCustomCategory } = useGetIsCustomCategory(category);

    // TODO: refactor,on FilterGames lvl to avoid such ternary.
    // each case (custom category / category / provider) should have a wrapper
    // like ProviderSection. and this logic should be on wrapper lvl
    const customCategoryPagination = usePlexCustomCategoryPagination({
      casinoType,
      category,
      pageSize
    });
    const categoryPagination = usePlexCategoryPagination({
      casinoType,
      category,
      pageSize,
      isCollections: provider === WIDGET_TYPES.COLLECTIONS
    });
    const providerPagination = usePlexProviderPagination({
      casinoType,
      provider,
      pageSize,
      isCollections: provider === WIDGET_TYPES.COLLECTIONS
    });

    const paginationData = useMemo<PaginationData>(() => {
      if (category) {
        return isCustomCategory ? customCategoryPagination : categoryPagination;
      }
      return providerPagination;
    }, [
      category,
      isCustomCategory,
      customCategoryPagination,
      categoryPagination,
      providerPagination
    ]);

    const {
      page,
      handlePageChange,
      isAllGamesLoaded,
      games,
      filteredMultiSectionGames,
      isLoading,
      isInitialDataLoaded
    } = paginationData;

    const isFiltered = Boolean(
      (isInitialDataLoaded && (games || filteredMultiSectionGames)) ||
        isAllGamesLoaded
    );

    return {
      games: games || [],
      isFiltered,
      page,
      filteredMultiSectionGames: filteredMultiSectionGames || [],
      handlePageChange,
      isAllGamesLoaded,
      isLoading
    };
  };

export const useGamesFilterAndPagination = IS_USE_PLEX_API
  ? usePlexGamesFilterAndPagination
  : useOldGamesFilterAndPagination;
